import React from "react";
import { DatePicker } from "@ui/DatePicker";
import {
  parseAbsolute,
  toCalendarDate,
  toCalendarDateTime,
  type DateValue,
  type ZonedDateTime,
} from "@internationalized/date";

interface Props {
  label: string;
  boundaries: { startsAt: number; endsAt: number };
  disabled?: boolean;
  timezone: string;
  error?: string;
  chosenDate: DateValue | null;
  isEndOfDay?: boolean;
  defaultDate: DateValue | null;
  onSetChosenDate: (date: DateValue | null) => void;
  isDateUnavailable: (
    newDate: DateValue,
    oldDate: DateValue | null,
    startBoundary: ZonedDateTime,
    endBoundary: ZonedDateTime,
  ) => boolean;
}
export const timeToDateBoundaries = (startsAt: number, endsAt: number, timezone: string) => {
  const startDate = new Date(startsAt);
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date(endsAt);
  endDate.setHours(23, 59, 59, 999);

  return {
    startBoundary: parseAbsolute(startDate.toISOString(), timezone),
    endBoundary: parseAbsolute(endDate.toISOString(), timezone),
  };
};

const EventCalendar = ({
  label,
  timezone,
  error,
  isEndOfDay = false,
  boundaries,
  chosenDate,
  onSetChosenDate,
  isDateUnavailable,
  defaultDate,
  disabled = false,
}: Props) => {
  const { startBoundary, endBoundary } = timeToDateBoundaries(
    boundaries.startsAt,
    boundaries.endsAt,
    timezone,
  );

  const handleSetChosenDate = (value: DateValue | null) => {
    const resValue = value;
    let zonedDate: ZonedDateTime | null;
    try {
      if (resValue === null) {
        zonedDate = null;
      } else if (isEndOfDay) {
        const endOfDayDate = resValue.set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
        const date = toCalendarDateTime(endOfDayDate).toDate(timezone);
        zonedDate = parseAbsolute(date.toISOString(), timezone);
      } else {
        const beginningOfDayDate = resValue.set({
          hour: 0,
          minute: 0,
          second: 0,
        });
        const date = toCalendarDate(beginningOfDayDate).toDate(timezone);
        zonedDate = parseAbsolute(date.toISOString(), timezone);
      }
    } catch (e) {
      onSetChosenDate(null);
      return;
    }
    onSetChosenDate(zonedDate);
  };

  const revertable = defaultDate?.toString() !== chosenDate?.toString();

  return (
    <div>
      <DatePicker
        id=""
        label={label}
        disabled={disabled}
        dateOptions={{
          shouldCloseOnSelect: true,
          hideTimeZone: true,
          defaultValue: defaultDate,
          granularity: "day",
          minValue: startBoundary,
          maxValue: endBoundary,
          value: chosenDate,
          isDateUnavailable: newDate =>
            isDateUnavailable(newDate, chosenDate ?? startBoundary, startBoundary, endBoundary),
          onChange: handleSetChosenDate,
        }}
        error={error}
        required
        revertable={revertable}
        revert={() => onSetChosenDate(defaultDate)}
      />
    </div>
  );
};

export default EventCalendar;
